/* eslint-disable @typescript-eslint/no-use-before-define */

import { z } from "zod"
import { useCallback, useState } from "preact/hooks"
import { FORM_ERROR } from "final-form"
import { Field, Form, FormSpy } from "react-final-form"
import css from "../style.module.scss"
import { $user } from "../hooks/user"
import { goBack, replace } from "../hooks/navigation"
import { t } from "../hooks/translations"

const forgotSchema = z.object({
  email: z.string().email().trim(),
})

type ForgotValues = z.infer<typeof forgotSchema>

export const ForgotScreen = function ForgotScreen() {
  const [success, setSuccess] = useState(false)

  return <div>{success ? <Success /> : <Forgot onSuccess={() => setSuccess(true)} />}</div>
}

type ForgotProps = {
  onSuccess: () => void
}

const Forgot = function Forgot({ onSuccess }: ForgotProps) {
  const [initialValues] = useState<ForgotValues>(() => ({
    email: $user.get().email,
  }))

  const onSubmit = useCallback(
    async (values: ForgotValues) => {
      const result = forgotSchema.safeParse(values)
      if (!result.success) {
        return result.error.flatten().fieldErrors
      }

      try {
        const response = await fetch("/sign_up/password/forgot", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            user: {
              email: result.data.email,
            },
          }),
        })

        const json = await response.json()
        if (json.success) {
          onSuccess()
          return {}
        }

        if (json.errors != null) {
          return { ...json, [FORM_ERROR]: json.errors }
        }

        return json
      } catch (error) {
        return { [FORM_ERROR]: String(error) }
      }
    },
    [onSuccess],
  )

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ submitting, submitError, handleSubmit }) => (
        <form className={css.signup} onSubmit={handleSubmit}>
          <FormSpy
            subscription={{ values: true }}
            onChange={(state) => {
              $user.setKey("email", state.values.email)
            }}
          />
          <p className={css.paragraph} style={{ marginBottom: 16 }}>
            {t("recover_your_password_for_mtn")}
          </p>
          <fieldset className={css["signup-fieldset"]}>
            <Field<string>
              name="email"
              render={({ input, meta }) => (
                <label htmlFor="signin-email">
                  <input
                    {...input}
                    className={css["signup-input"]}
                    type="email"
                    autoComplete="email"
                    placeholder="Email address"
                    id="signin-email"
                    disabled={submitting}
                  />
                  {(meta.error || meta.submitError) && meta.touched && (
                    <div className={css["signup-field-error"]}>
                      {meta.error || meta.submitError}
                    </div>
                  )}
                </label>
              )}
            />
          </fieldset>

          {submitError && <div className={css["submit-error"]}>{submitError}</div>}

          <button
            className={css.button}
            style={{ marginBottom: 16 }}
            type="submit"
            disabled={submitting}
          >
            {t("recover_password")}
          </button>

          <p className={css["signup-offer"]}>
            {t("dont_have_account")}{" "}
            <a
              href="/sign_in"
              onClick={(ev) => {
                ev.preventDefault()
                goBack()
                replace("SignUp")
              }}
            >
              {t("sign_up")}
            </a>
          </p>
        </form>
      )}
    />
  )
}

const Success = function Success() {
  return (
    <div>
      <p className={css["submit-success"]}>A reset link has been sent to your email address.</p>
      <button className={css.button} type="button" onClick={() => goBack()}>
        Continue
      </button>
    </div>
  )
}
